import React, { useCallback, useEffect, useState } from "react";

import { HubEntityPermissions, HubEntityScope, HubPermissions } from "../../models/hubPermissions";
import { siteLocationPrefix } from "../../constants";
import * as styles from "./TestStyle.module.css";

type HubPermissionsResponse = Readonly<{
  accessLevel?: number;
  crmDerived: HubPermissions;
  effective: HubPermissions;
  override?: HubPermissions;
}>;

type EntityPermissionsEditorProps = Readonly<{
  label: string;
  permissions: HubEntityPermissions;
  onChange: (permissions: HubEntityPermissions) => void;
}>;

const scopes: HubEntityScope[] = ["none", "own", "all"];
const booleanPermissions = [
  ["canAccessAdministratorSettings", "Administrator Settings"],
  ["canEditOwnContactDetails", "Edit Own Contact Details"],
  ["canEditStudioDetails", "Edit Studio Details"],
  ["canSubmitIdApplication", "Submit ID@XBOX Application"],
  ["canSubmitPCEnrollment", "Submit Partner Center Enrollment"],
] as const;

const EntityPermissionsEditor = ({
  label,
  permissions,
  onChange,
}: EntityPermissionsEditorProps) => {
  const [entityId, setEntityId] = useState("");

  const setScope = (action: "view" | "edit" | "submit", scope: HubEntityScope) =>
    onChange({ ...permissions, [action]: scope });

  const addGrant = () => {
    const normalizedId = entityId.trim().toLowerCase();
    if (!normalizedId) {
      return;
    }
    onChange({
      ...permissions,
      additionalEntityPermissions: {
        ...permissions.additionalEntityPermissions,
        [normalizedId]: { canEdit: false, canSubmit: false },
      },
    });
    setEntityId("");
  };

  return (
    <section className={styles.hubPermissionSection}>
      <h3>{label}</h3>
      <div className={styles.hubPermissionScopes}>
        {(["view", "edit", "submit"] as const).map(action => (
          <label className={styles.hubPermissionScope} key={action}>
            <span>{action}</span>
            <select
              className={styles.input}
              value={permissions[action]}
              onChange={event => setScope(action, event.target.value as HubEntityScope)}
            >
              {scopes.map(scope => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <div className={styles.hubEntityGrant}>
        <input
          className={styles.input}
          aria-label={`${label} entity ID`}
          placeholder="Entity ID"
          value={entityId}
          onChange={event => setEntityId(event.target.value)}
        />
        <button className={styles.testButton} type="button" onClick={addGrant}>
          Add entity grant
        </button>
      </div>
      {Object.entries(permissions.additionalEntityPermissions).map(([id, grant]) => (
        <div className={styles.row} key={id}>
          <span>{id}</span>
          <label>
            <input
              type="checkbox"
              checked={grant.canEdit}
              onChange={event =>
                onChange({
                  ...permissions,
                  additionalEntityPermissions: {
                    ...permissions.additionalEntityPermissions,
                    [id]: { ...grant, canEdit: event.target.checked },
                  },
                })
              }
            />
            Edit
          </label>
          <label>
            <input
              type="checkbox"
              checked={grant.canSubmit}
              onChange={event =>
                onChange({
                  ...permissions,
                  additionalEntityPermissions: {
                    ...permissions.additionalEntityPermissions,
                    [id]: { ...grant, canSubmit: event.target.checked },
                  },
                })
              }
            />
            Submit
          </label>
          <button
            className={styles.testButtonDanger}
            type="button"
            onClick={() => {
              const remaining = Object.fromEntries(
                Object.entries(permissions.additionalEntityPermissions).filter(
                  ([entityId]) => entityId !== id
                )
              );
              onChange({ ...permissions, additionalEntityPermissions: remaining });
            }}
          >
            Remove
          </button>
        </div>
      ))}
    </section>
  );
};

export const TestHubPermissions = () => {
  const [response, setResponse] = useState<HubPermissionsResponse>();
  const [custom, setCustom] = useState<HubPermissions>();
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const result = await fetch(`${siteLocationPrefix}/test/hub-permissions`);
    if (!result.ok) {
      setError(`Load failed: ${result.status}`);
      return;
    }
    const next = (await result.json()) as HubPermissionsResponse;
    setResponse(next);
    setCustom(next.effective);
    setError("");
  }, []);

  useEffect(() => {
    load().catch(() => setError("Load failed"));
  }, [load]);

  const save = async (body: object) => {
    const result = await fetch(`${siteLocationPrefix}/test/hub-permissions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!result.ok) {
      setError(`Save failed: ${result.status}`);
      return;
    }
    const next = (await result.json()) as HubPermissionsResponse;
    setResponse(next);
    setCustom(next.effective);
    setError("");
  };

  if (!response || !custom) {
    return (
      <div className={styles.generalActionsWrapper}>
        <h3>Hub permission override</h3>
        <p>{error || "Loading Hub permissions..."}</p>
      </div>
    );
  }

  const setEntityPermissions = (
    entity: "gameConcepts" | "pitches",
    permissions: HubEntityPermissions
  ) => setCustom({ ...custom, [entity]: permissions });

  const applyCustomOverride = () =>
    save({ permissions: custom }).catch(() => setError("Save failed"));

  return (
    <div className={`${styles.generalActionsWrapper} ${styles.hubPermissionsWrapper}`}>
      <h3>Hub permission override</h3>
      <p className={styles.hubPermissionStatus}>
        CRM role: {response.accessLevel ?? "unset"} | Override:{" "}
        {response.override ? "enabled" : "disabled"}
      </p>
      <div className={styles.hubProfileActions}>
        {(["crm", "admin", "gameManager", "user", "noHubAccess"] as const).map(profile => (
          <button
            className={styles.testButton}
            key={profile}
            type="button"
            onClick={() => save({ profile }).catch(() => setError("Save failed"))}
          >
            {profile === "crm"
              ? "Reset to CRM"
              : profile === "noHubAccess"
              ? "No Hub Access"
              : profile}
          </button>
        ))}
      </div>
      <EntityPermissionsEditor
        label="Game Concepts"
        permissions={custom.gameConcepts}
        onChange={permissions => setEntityPermissions("gameConcepts", permissions)}
      />
      <EntityPermissionsEditor
        label="Pitches"
        permissions={custom.pitches}
        onChange={permissions => setEntityPermissions("pitches", permissions)}
      />
      <section className={styles.hubPermissionSection}>
        <h3>Roles</h3>
        <div className={styles.hubRoles}>
          {booleanPermissions.map(([permission, label]) => (
            <label className={styles.hubRole} key={permission}>
              <input
                type="checkbox"
                checked={custom[permission]}
                onChange={event => setCustom({ ...custom, [permission]: event.target.checked })}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <button
          className={`${styles.testButton} ${styles.hubPermissionApply}`}
          type="button"
          onClick={applyCustomOverride}
        >
          Apply custom override
        </button>
      </section>
      {error && <p>{error}</p>}
    </div>
  );
};
